import React, { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, apiErr } from "@/api";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { StatusBadge } from "@/components/StatusBadge";
import { STATUS_STYLES, STATUS_LABEL, inr, inrFull } from "@/utils";
import { toast } from "sonner";
import { UploadCloud, Download, Calculator, Trash2, FileSignature, ShieldCheck, User as UserIcon, Zap, Search as SearchIcon } from "lucide-react";

const STATUSES = ["available", "reserved", "booked", "agreement_done", "registered", "sold", "cancelled", "blocked"];
const CONFIGS = ["Studio", "1 BHK", "1.5 BHK", "2 BHK", "2.5 BHK", "3 BHK", "3.5 BHK", "4 BHK", "5 BHK", "Duplex", "Penthouse", "Simplex"];
const FACINGS = ["North", "South", "East", "West", "North-East", "North-West", "South-East", "South-West"];
const UNIT_TYPES = ["flat", "row_house", "shop", "office", "showroom"];

const CHARGE_KEYS = ["plc", "floor_rise_charges", "other_charges", "gst_amount", "stamp_duty", "registration_charges"];

function chipLabel(u) {
  const suf = u.unit_number.split("-").pop();
  return suf || u.unit_number;
}

export default function Inventory() {
  const qc = useQueryClient();
  const { data: projects = [] } = useQuery({
    queryKey: ["projects"],
    queryFn: async () => (await api.get("/projects")).data,
  });
  const [projectId, setProjectId] = useState("");
  useEffect(() => {
    if (!projectId && projects.length) setProjectId(projects[0].id);
  }, [projects, projectId]);

  const { data: units = [] } = useQuery({
    queryKey: ["units", projectId],
    queryFn: async () => (await api.get("/units", { params: { project_id: projectId } })).data,
    enabled: !!projectId,
  });

  const [selectedUnit, setSelectedUnit] = useState(null);
  const importInput = useRef(null);
  const [importing, setImporting] = useState(false);

  const grouped = useMemo(() => {
    const g = {};
    for (const u of units) {
      g[u.tower] = g[u.tower] || {};
      g[u.tower][u.floor] = g[u.tower][u.floor] || [];
      g[u.tower][u.floor].push(u);
    }
    return g;
  }, [units]);

  const stats = useMemo(() => {
    const s = { total: units.length, value: 0 };
    for (const u of units) {
      s[u.status] = (s[u.status] || 0) + 1;
      s.value += u.final_selling_price || u.price || 0;
    }
    return s;
  }, [units]);

  async function updateStatus(unitId, newStatus, reason) {
    try {
      await api.patch(`/units/${unitId}`, { status: newStatus, _reason: reason });
      toast.success(`Unit marked ${STATUS_LABEL[newStatus]}`);
      qc.invalidateQueries({ queryKey: ["units", projectId] });
      qc.invalidateQueries({ queryKey: ["dashboard-kpi"] });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  }

  async function handleImport(e) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!projectId) { toast.error("Select a project first"); return; }
    setImporting(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("project_id", projectId);
    try {
      const { data } = await api.post("/units/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Import complete — created ${data.created}, updated ${data.updated}${data.errors?.length ? `, ${data.errors.length} rows skipped` : ""}`);
      if (data.errors?.length) console.warn("Import warnings:", data.errors);
      qc.invalidateQueries({ queryKey: ["units", projectId] });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Import failed");
    } finally { setImporting(false); }
  }

  function downloadTemplate() {
    const header = ["tower", "wing", "floor", "unit_number", "unit_type", "configuration", "facing",
      "carpet_area", "built_up_area", "super_built_up_area", "balcony_area", "terrace_area",
      "parking", "parking_details", "rate_per_sqft", "base_price", "plc", "floor_rise_charges",
      "other_charges", "gst_amount", "stamp_duty", "registration_charges", "final_selling_price", "status"];
    const sample = ["A", "A", 1, "A-0101", "flat", "2 BHK", "East",
      850, 1000, 1100, 60, 0, 1, "Covered", 8500, 0, 250000, 100000,
      50000, 467500, 350000, 30000, 0, "available"];
    const csv = [header.join(","), sample.join(",")].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "unit_import_template.csv"; a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="space-y-6" data-testid="inventory-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.15em] font-semibold text-emerald-700">Inventory</div>
          <h1 className="font-heading text-3xl font-bold mt-1">Interactive Sales Board</h1>
          <p className="text-sm text-zinc-500 mt-1">Every unit has its own size, configuration and price. Click a unit to edit everything.</p>
        </div>
        <div className="flex gap-2 items-center flex-wrap">
          <Select value={projectId} onValueChange={setProjectId}>
            <SelectTrigger className="w-72" data-testid="inventory-project-select"><SelectValue placeholder="Select project" /></SelectTrigger>
            <SelectContent>
              {projects.map((p) => (
                <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={downloadTemplate} data-testid="download-template-btn">
            <Download size={13} className="mr-1" /> Template
          </Button>
          <BulkPricingButton projectId={projectId} units={units} onDone={() => qc.invalidateQueries({ queryKey: ["units", projectId] })} />
          <label className="inline-flex items-center gap-2 px-3 h-10 bg-emerald-700 hover:bg-emerald-800 text-white rounded-md text-sm cursor-pointer transition-colors" data-testid="import-units-label">
            <UploadCloud size={14} /> {importing ? "Importing…" : "Import Excel / CSV"}
            <input ref={importInput} type="file" accept=".xlsx,.xls,.csv" hidden onChange={handleImport} disabled={importing} data-testid="import-units-input" />
          </label>
        </div>
      </div>

      <Card className="bg-white border-zinc-200">
        <CardContent className="p-4 flex flex-wrap gap-3 items-center">
          <span className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold mr-2">Legend:</span>
          {STATUSES.map((s) => (
            <div key={s} className="flex items-center gap-2 text-xs">
              <span className={`w-4 h-4 rounded-sm border ${STATUS_STYLES[s]}`} />
              <span className="text-zinc-700">{STATUS_LABEL[s]}</span>
              <span className="text-zinc-400 font-mono">{stats[s] || 0}</span>
            </div>
          ))}
          <div className="ml-auto text-xs font-mono text-zinc-500">
            Total <b className="text-zinc-800">{stats.total || 0}</b> · Value <b className="text-zinc-800">{inr(stats.value)}</b>
          </div>
        </CardContent>
      </Card>

      {Object.entries(grouped).map(([tower, floors]) => (
        <Card key={tower} className="bg-white border-zinc-200">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading text-lg font-semibold">Tower {tower}</h3>
              <span className="text-xs text-zinc-500 font-mono">{Object.values(floors).flat().length} units</span>
            </div>
            <div className="space-y-1.5">
              {Object.entries(floors).sort((a, b) => Number(b[0]) - Number(a[0])).map(([floor, us]) => (
                <div key={floor} className="flex items-start gap-2">
                  <div className="text-[11px] font-mono text-zinc-500 w-12 shrink-0 pt-3">F{String(floor).padStart(2, "0")}</div>
                  <div className="grid gap-1.5 flex-1" style={{ gridTemplateColumns: `repeat(${Math.max(us.length, 6)}, minmax(0, 1fr))` }}>
                    {us.map((u) => {
                      const canQuickAssign = ["available", "reserved"].includes(u.status) && !u.customer_name;
                      const openEditor = () => setSelectedUnit({ ...u, _reason: "" });
                      if (canQuickAssign) {
                        return (
                          <QuickAssignChip
                            key={u.id}
                            unit={u}
                            projectId={projectId}
                            onOpenEditor={openEditor}
                            onAssigned={() => qc.invalidateQueries({ queryKey: ["units", projectId] })}
                          />
                        );
                      }
                      return <UnitChip key={u.id} unit={u} onClick={openEditor} />;
                    })}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      ))}

      {!units.length && projectId && (
        <Card className="bg-white border-dashed border-zinc-300">
          <CardContent className="p-16 text-center text-sm text-zinc-500">
            No units yet. Use <b>Import Excel/CSV</b> above, or open the project and click <b>Bulk Create Tower</b>.
            <br />Every unit can then be independently edited with its own size, configuration and price.
          </CardContent>
        </Card>
      )}

      <UnitEditor
        unit={selectedUnit}
        onClose={() => setSelectedUnit(null)}
        onSaved={() => {
          qc.invalidateQueries({ queryKey: ["units", projectId] });
          qc.invalidateQueries({ queryKey: ["dashboard-kpi"] });
          setSelectedUnit(null);
        }}
        updateStatus={updateStatus}
      />
    </div>
  );
}

function UnitChip({ unit: u, onClick, popoverTrigger = false }) {
  const chipContent = (
    <>
      {u.sale_deed_completed && (
        <span className="absolute top-0.5 right-0.5 text-emerald-600" title="Sale Deed Registered · Revenue Recognized">
          <ShieldCheck size={10} />
        </span>
      )}
      {popoverTrigger && (
        <span className="absolute top-0.5 left-0.5 text-emerald-700 opacity-0 group-hover:opacity-100 transition-opacity" title="Quick-assign customer">
          <Zap size={9} />
        </span>
      )}
      <span className="font-semibold leading-none">{chipLabel(u)}</span>
      {u.customer_name ? (
        <span
          className="text-[9px] font-medium leading-tight mt-0.5 truncate max-w-full px-0.5"
          data-testid={`unit-chip-customer-${u.unit_number}`}
        >
          {(() => {
            const parts = (u.customer_name || "").trim().split(/\s+/);
            if (parts.length === 1) return parts[0];
            return `${parts[0]} ${parts[parts.length - 1]}`;
          })()}
        </span>
      ) : (
        <span className="text-[9px] font-normal opacity-80 leading-none mt-0.5">
          {u.configuration ? `${u.configuration.replace(" BHK", "B")} · ` : ""}{u.super_built_up_area || u.area_sqft || 0}
        </span>
      )}
    </>
  );
  const title = (
    `${u.unit_number} · ${u.configuration || u.unit_type} · ${u.super_built_up_area || u.area_sqft || 0} sqft · ${STATUS_LABEL[u.status]} · ${inrFull(u.final_selling_price || u.price)}`
    + (u.customer_name ? `\nCustomer: ${u.customer_name}${u.customer_phone ? " · " + u.customer_phone : ""}` : "")
    + (u.sale_deed_completed ? "\nSale Deed Registered · Revenue Recognized" : "")
    + (popoverTrigger ? "\n(Click to quick-assign customer)" : "")
  );
  return (
    <button
      onClick={onClick}
      className={`unit-chip flex-col !min-h-[62px] py-1 relative group ${STATUS_STYLES[u.status]}`}
      title={title}
      data-testid={`unit-chip-${u.unit_number}`}
    >
      {chipContent}
    </button>
  );
}


function QuickAssignChip({ unit, projectId, onOpenEditor, onAssigned }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);

  const { data: customers = [] } = useQuery({
    queryKey: ["customers-quickassign", projectId],
    queryFn: async () => (await api.get("/customers", { params: { project_id: projectId } })).data,
    enabled: open && !!projectId,
  });

  const options = useMemo(() => {
    const eligible = customers.filter((c) => !c.unit_id && c.active !== false);
    if (!q.trim()) return eligible.slice(0, 30);
    const s = q.toLowerCase();
    return eligible.filter((c) =>
      (c.name || "").toLowerCase().includes(s) ||
      (c.phone || "").toLowerCase().includes(s) ||
      (c.pan || "").toLowerCase().includes(s)
    ).slice(0, 30);
  }, [customers, q]);

  async function assign(c) {
    setBusy(true);
    try {
      await api.patch(`/customers/${c.id}`, {
        unit_id: unit.id,
        _reason: `Quick-assigned to unit ${unit.unit_number} from Sales Board`,
      });
      toast.success(`${c.name} assigned to unit ${unit.unit_number}`);
      ["units", "customers", "customers-quickassign", "customers-wf", "dashboard-kpi", "dashboard-units"]
        .forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
      onAssigned?.();
      setOpen(false);
      setQ("");
    } catch (e) {
      toast.error(apiErr(e, "Failed to assign customer"));
    } finally { setBusy(false); }
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          className={`unit-chip flex-col !min-h-[62px] py-1 relative group ${STATUS_STYLES[unit.status]}`}
          title={`${unit.unit_number} · ${unit.configuration || unit.unit_type} · ${STATUS_LABEL[unit.status]}\nClick to quick-assign a customer`}
          data-testid={`unit-chip-${unit.unit_number}`}
        >
          <span className="absolute top-0.5 left-0.5 text-emerald-700/70 group-hover:text-emerald-700" title="Quick-assign">
            <Zap size={9} />
          </span>
          <span className="font-semibold leading-none">{chipLabel(unit)}</span>
          <span className="text-[9px] font-normal opacity-80 leading-none mt-0.5">
            {unit.configuration ? `${unit.configuration.replace(" BHK", "B")} · ` : ""}{unit.super_built_up_area || unit.area_sqft || 0}
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-80 p-0"
        align="start"
        data-testid={`quick-assign-popover-${unit.unit_number}`}
      >
        <div className="px-3 pt-3 pb-2 border-b border-zinc-200">
          <div className="text-[10px] uppercase tracking-widest text-emerald-700 font-semibold">Quick Assign · Unit {unit.unit_number}</div>
          <div className="text-[11px] text-zinc-500 mt-0.5">
            {unit.configuration || unit.unit_type} · {unit.super_built_up_area || unit.area_sqft || 0} sqft · {inrFull(unit.final_selling_price || unit.price)}
          </div>
          <div className="relative mt-2">
            <SearchIcon size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none" />
            <Input
              autoFocus
              placeholder="Search customer by name, phone, PAN…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="h-8 pl-7 text-xs"
              data-testid={`quick-assign-search-${unit.unit_number}`}
            />
          </div>
        </div>
        <div className="max-h-64 overflow-y-auto divide-y divide-zinc-100">
          {options.length === 0 && (
            <div className="text-center text-xs text-zinc-500 py-6 px-3">
              {customers.length === 0
                ? "Loading customers…"
                : "No unassigned customers match. Add a new customer from the Customers page first."}
            </div>
          )}
          {options.map((c) => (
            <button
              key={c.id}
              disabled={busy}
              onClick={() => assign(c)}
              className="w-full text-left px-3 py-2 hover:bg-emerald-50 focus:bg-emerald-50 focus:outline-none disabled:opacity-50 transition-colors"
              data-testid={`quick-assign-pick-${c.id}`}
            >
              <div className="text-sm font-medium truncate">{c.name}</div>
              <div className="text-[11px] text-zinc-500 flex gap-2">
                {c.phone && <span>{c.phone}</span>}
                {c.pan && <span>· PAN {c.pan}</span>}
              </div>
            </button>
          ))}
        </div>
        <div className="border-t border-zinc-200 px-3 py-2 flex items-center justify-between">
          <button
            className="text-[11px] text-zinc-500 hover:text-zinc-800"
            onClick={() => { setOpen(false); onOpenEditor(); }}
            data-testid={`quick-assign-open-editor-${unit.unit_number}`}
          >
            Open full editor →
          </button>
          <span className="text-[10px] text-zinc-400 font-mono">Sales Board</span>
        </div>
      </PopoverContent>
    </Popover>
  );
}


function UnitEditor({ unit, onClose, onSaved, updateStatus }) {  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { setForm(unit); }, [unit]);

  const auto = useMemo(() => {
    if (!form) return null;
    const sba = Number(form.super_built_up_area) || Number(form.built_up_area) || Number(form.carpet_area) || 0;
    const rate = Number(form.rate_per_sqft) || 0;
    const total = rate > 0 && sba > 0 ? rate * sba : Number(form.total_unit_price) || Number(form.base_price) || 0;
    const charges = CHARGE_KEYS.reduce((s, k) => s + (Number(form[k]) || 0), 0);
    const finalPrice = total + charges;
    return { sba, total, charges, finalPrice };
  }, [form]);

  if (!form) return null;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  async function save() {
    setBusy(true);
    try {
      const payload = { ...form };
      // Strip legacy keys from the wire (backend will recompute)
      delete payload._id;
      const reason = payload._reason || "";
      delete payload._reason;
      const numFields = ["floor", "carpet_area", "built_up_area", "super_built_up_area",
        "balcony_area", "terrace_area", "parking", "base_price", "rate_per_sqft",
        "total_unit_price", "plc", "floor_rise_charges", "other_charges",
        "gst_amount", "stamp_duty", "registration_charges", "final_selling_price"];
      for (const k of numFields) if (payload[k] !== undefined && payload[k] !== "") payload[k] = Number(payload[k]) || 0;
      if (reason) payload._reason = reason;
      await api.patch(`/units/${form.id}`, payload);
      toast.success("Unit saved");
      onSaved();
    } catch (e) {
      toast.error(apiErr(e, "Save failed"));
    } finally { setBusy(false); }
  }

  async function handleDelete() {
    if (!form?.id) return;
    if (!window.confirm(`Delete unit ${form.unit_number}?\n\nIf the unit is booked/registered/sold or linked to a customer, deletion will be blocked (change status to Cancelled/Blocked instead).`)) return;
    const reason = window.prompt("Reason for deletion (audit)?", "") || "Deleted from unit editor";
    setBusy(true);
    try {
      await api.delete(`/units/${form.id}`, { params: { reason } });
      toast.success("Unit deleted");
      onSaved();
    } catch (e) {
      const detail = apiErr(e);
      if (detail.toLowerCase().includes("cannot delete") && detail.toLowerCase().includes("force")) {
        if (window.confirm(`${detail}\n\nAs Admin, force delete anyway? Any linked customer will be unlinked from the unit.`)) {
          try {
            await api.delete(`/units/${form.id}`, { params: { force: true, reason: reason + " (force)" } });
            toast.success("Unit force-deleted");
            onSaved();
          } catch (er) { toast.error(apiErr(er)); }
        }
      } else { toast.error(detail); }
    } finally { setBusy(false); }
  }

  const field = (k, label, opts = {}) => (
    <div>
      <Label className="text-[11px] uppercase tracking-widest text-zinc-500">{label}</Label>
      <Input
        type={opts.type || "text"}
        value={form[k] ?? ""}
        onChange={(e) => set(k, e.target.value)}
        className="mt-1 h-9 border-zinc-300"
        data-testid={`unit-${k}`}
      />
    </div>
  );

  return (
    <Dialog open={!!unit} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="unit-editor">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-3">
            Unit {form.unit_number}
            <StatusBadge status={form.status} />
            {form.configuration && <span className="text-xs font-normal text-zinc-500">· {form.configuration}</span>}
          </DialogTitle>
        </DialogHeader>

        {form.customer_id && (
          <SaleDeedBar
            unit={form}
            onDone={onSaved}
          />
        )}

        <Tabs defaultValue="basic">
          <TabsList className="bg-zinc-100">
            <TabsTrigger value="basic" data-testid="ue-tab-basic">Basic</TabsTrigger>
            <TabsTrigger value="areas" data-testid="ue-tab-areas">Areas</TabsTrigger>
            <TabsTrigger value="pricing" data-testid="ue-tab-pricing">Pricing</TabsTrigger>
            <TabsTrigger value="status" data-testid="ue-tab-status">Status</TabsTrigger>
          </TabsList>

          <TabsContent value="basic" className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-4">
            {field("unit_number", "Unit Number")}
            {field("tower", "Tower")}
            {field("wing", "Wing")}
            {field("floor", "Floor", { type: "number" })}
            <div>
              <Label className="text-[11px] uppercase tracking-widest text-zinc-500">Unit Type</Label>
              <Select value={form.unit_type} onValueChange={(v) => set("unit_type", v)}>
                <SelectTrigger className="mt-1 h-9" data-testid="unit-unit_type"><SelectValue /></SelectTrigger>
                <SelectContent>{UNIT_TYPES.map((t) => <SelectItem key={t} value={t}>{t.replace("_", " ")}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-widest text-zinc-500">Configuration</Label>
              <Select value={form.configuration || ""} onValueChange={(v) => set("configuration", v)}>
                <SelectTrigger className="mt-1 h-9" data-testid="unit-configuration"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{CONFIGS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-widest text-zinc-500">Facing</Label>
              <Select value={form.facing || ""} onValueChange={(v) => set("facing", v)}>
                <SelectTrigger className="mt-1 h-9"><SelectValue placeholder="Facing" /></SelectTrigger>
                <SelectContent>{FACINGS.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {field("parking", "Parking Slots", { type: "number" })}
            {field("parking_details", "Parking Details")}
            {field("construction_stage", "Construction Stage")}
            <div className="col-span-full">
              {field("floor_plan_url", "Floor Plan URL")}
            </div>
          </TabsContent>

          <TabsContent value="areas" className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-4">
            {field("carpet_area", "Carpet Area (sqft)", { type: "number" })}
            {field("built_up_area", "Built-up Area (sqft)", { type: "number" })}
            {field("super_built_up_area", "Super Built-up (sqft)", { type: "number" })}
            {field("balcony_area", "Balcony Area (sqft)", { type: "number" })}
            {field("terrace_area", "Terrace Area (sqft)", { type: "number" })}
            <div className="col-span-full text-xs text-zinc-500 bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2">
              Effective saleable area (used for pricing): <b className="font-mono">{auto.sba} sqft</b>
              — chosen as Super Built-up ▸ Built-up ▸ Carpet, whichever is set first.
            </div>
          </TabsContent>

          <TabsContent value="pricing" className="mt-4 space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {field("rate_per_sqft", "Rate ₹/sqft", { type: "number" })}
              {field("base_price", "Base Price (₹)", { type: "number" })}
              {field("plc", "PLC — Preferred Location (₹)", { type: "number" })}
              {field("floor_rise_charges", "Floor Rise (₹)", { type: "number" })}
              {field("other_charges", "Other Charges (₹)", { type: "number" })}
              {field("gst_amount", "GST (₹)", { type: "number" })}
              {field("stamp_duty", "Stamp Duty (₹)", { type: "number" })}
              {field("registration_charges", "Registration (₹)", { type: "number" })}
            </div>

            <div className="bg-zinc-950 text-white rounded-md p-4 font-mono text-sm space-y-1" data-testid="unit-price-breakdown">
              <div className="flex justify-between"><span className="opacity-70">Total Unit Price (rate × area)</span><span>{inrFull(auto.total)}</span></div>
              <div className="flex justify-between"><span className="opacity-70">Additional Charges</span><span>+ {inrFull(auto.charges)}</span></div>
              <div className="flex justify-between border-t border-white/20 pt-2 mt-2 text-emerald-300 font-semibold">
                <span>Auto-calculated Final Selling Price</span><span>{inrFull(auto.finalPrice)}</span>
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 items-end">
              <div className="col-span-2">
                <Label className="text-[11px] uppercase tracking-widest text-zinc-500">Manual Final Selling Price Override (₹)</Label>
                <Input
                  type="number"
                  value={form.final_selling_price || ""}
                  onChange={(e) => set("final_selling_price", e.target.value)}
                  disabled={!form.price_override}
                  className="mt-1 h-9 border-zinc-300"
                  data-testid="unit-final_selling_price"
                />
              </div>
              <div className="flex items-center gap-2 pb-1">
                <Checkbox
                  id="override"
                  checked={!!form.price_override}
                  onCheckedChange={(v) => set("price_override", !!v)}
                  data-testid="unit-price_override"
                />
                <Label htmlFor="override">Manual override</Label>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="status" className="mt-4 space-y-3">
            <div className="text-sm text-zinc-600">Change the sales status of this unit — this instantly reflects in the sales board and dashboards.</div>
            <div className="flex flex-wrap gap-2">
              {STATUSES.map((s) => (
                <Button
                  key={s} variant="outline" size="sm"
                  className={`border ${STATUS_STYLES[s]} hover:opacity-90`}
                  onClick={() => updateStatus(form.id, s, form._reason)}
                  data-testid={`set-status-${s}`}
                >
                  {STATUS_LABEL[s]}
                </Button>
              ))}
            </div>
          </TabsContent>
        </Tabs>

        <div className="mt-3">
          <Label className="text-[11px] uppercase tracking-widest text-zinc-500">Reason for change (audit)</Label>
          <Input
            value={form._reason || ""}
            onChange={(e) => set("_reason", e.target.value)}
            placeholder="e.g., PLC updated for corner unit; buyer negotiated ₹2L discount"
            className="mt-1 h-9 border-zinc-300"
            data-testid="unit-reason"
          />
        </div>

        <DialogFooter className="flex justify-between items-center">
          <Button variant="outline" onClick={handleDelete} disabled={busy}
                  className="text-red-600 border-red-200 hover:bg-red-50" data-testid="unit-delete">
            <Trash2 size={13} className="mr-1" /> Delete Unit
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button className="bg-emerald-700 hover:bg-emerald-800" disabled={busy} onClick={save} data-testid="unit-save">
              {busy ? "Saving…" : "Save changes"}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}



// ============ Sale Deed Bar — prominent action inside UnitEditor ============
function SaleDeedBar({ unit, onDone }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [sd, setSd] = useState({
    sale_deed_number: "",
    registration_number: "",
    registration_date: new Date().toISOString().slice(0, 10),
    stamp_duty: unit.stamp_duty || 0,
    registration_charges: unit.registration_charges || 0,
    final_gst: unit.gst_amount || 0,
    tds_deducted: 0,
    notes: "",
  });

  // Fetch summary from customer timeline to show collected amount
  const { data: timeline } = useQuery({
    queryKey: ["timeline-inv", unit.customer_id],
    queryFn: async () => (await api.get(`/customers/${unit.customer_id}/timeline`)).data,
    enabled: !!unit.customer_id,
  });

  const already = !!unit.sale_deed_completed;
  const collected = timeline?.summary?.total_received || 0;
  const finalPrice = timeline?.summary?.final_price || unit.final_selling_price || unit.price || 0;

  async function registerSD() {
    if (!sd.sale_deed_number) { toast.error("Sale Deed Number required"); return; }
    setBusy(true);
    try {
      await api.post("/sale-deeds", {
        ...sd,
        customer_id: unit.customer_id,
        project_id: unit.project_id,
        unit_id: unit.id,
        stamp_duty: +sd.stamp_duty || 0,
        registration_charges: +sd.registration_charges || 0,
        final_gst: +sd.final_gst || 0,
        tds_deducted: +sd.tds_deducted || 0,
      });
      toast.success("Sale Deed Done · Revenue Recognized ✓");
      ["units", "customers-wf", "pnl-page", "dashboard-kpi", "dashboard-units",
       "incomes", "receipts", "timeline", "expense-summary", "sale-deeds",
      ].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
      qc.invalidateQueries({ queryKey: ["timeline-inv", unit.customer_id] });
      setOpen(false);
      onDone?.();
    } catch (e) {
      toast.error(apiErr(e, "Failed to register sale deed"));
    } finally { setBusy(false); }
  }

  if (already) {
    return (
      <div className="mt-3 rounded-md border border-emerald-300 bg-emerald-50 px-3 py-2 flex items-center gap-3 flex-wrap" data-testid="sale-deed-bar-done">
        <ShieldCheck size={18} className="text-emerald-700 shrink-0" />
        <div className="flex-1 min-w-[180px]">
          <div className="text-sm font-semibold text-emerald-900">Sale Deed Registered · Revenue Recognized</div>
          <div className="text-[11px] text-emerald-800">
            Buyer <b>{unit.customer_name}</b>{unit.customer_phone ? " · " + unit.customer_phone : ""} — full collection moved from Customer Advances to Recognized Revenue.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-3 rounded-md border border-purple-300 bg-purple-50 px-3 py-2 flex items-center gap-3 flex-wrap" data-testid="sale-deed-bar">
      <UserIcon size={18} className="text-purple-800 shrink-0" />
      <div className="flex-1 min-w-[180px]">
        <div className="text-sm font-semibold text-purple-900">Linked Customer: {unit.customer_name}
          {unit.customer_phone && <span className="text-[11px] font-normal text-purple-700 ml-2">· {unit.customer_phone}</span>}
        </div>
        <div className="text-[11px] text-purple-800">
          Collected <b className="font-mono">{inrFull(collected)}</b> of <b className="font-mono">{inrFull(finalPrice)}</b> · currently sitting as <b>Customer Advances</b>.
        </div>
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
          <Button className="bg-purple-700 hover:bg-purple-800 text-white" data-testid="sale-deed-done-btn">
            <FileSignature size={13} className="mr-1" /> Sale Deed Done → Revenue
          </Button>
        </DialogTrigger>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle className="font-heading">Register Sale Deed · Auto-Recognize Revenue</DialogTitle>
          </DialogHeader>
          <div className="bg-amber-50 border border-amber-200 text-amber-900 rounded-md p-3 text-xs">
            Registering the Sale Deed will move <b>{inrFull(collected)}</b> from <b>Customer Advances</b> (liability) to <b>Recognized Revenue</b> in P&amp;L. Unit status becomes <b>Registered</b>.
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Sale Deed Number *</Label>
              <Input value={sd.sale_deed_number} onChange={(e) => setSd({ ...sd, sale_deed_number: e.target.value })} data-testid="sd-inv-number" />
            </div>
            <div>
              <Label>Registration Number</Label>
              <Input value={sd.registration_number} onChange={(e) => setSd({ ...sd, registration_number: e.target.value })} />
            </div>
            <div>
              <Label>Registration Date</Label>
              <Input type="date" value={sd.registration_date} onChange={(e) => setSd({ ...sd, registration_date: e.target.value })} />
            </div>
            <div>
              <Label>Stamp Duty (₹)</Label>
              <Input type="number" value={sd.stamp_duty} onChange={(e) => setSd({ ...sd, stamp_duty: e.target.value })} />
            </div>
            <div>
              <Label>Registration Charges (₹)</Label>
              <Input type="number" value={sd.registration_charges} onChange={(e) => setSd({ ...sd, registration_charges: e.target.value })} />
            </div>
            <div>
              <Label>Final GST Paid (₹)</Label>
              <Input type="number" value={sd.final_gst} onChange={(e) => setSd({ ...sd, final_gst: e.target.value })} />
            </div>
            <div>
              <Label>TDS Deducted (₹)</Label>
              <Input type="number" value={sd.tds_deducted} onChange={(e) => setSd({ ...sd, tds_deducted: e.target.value })} />
            </div>
            <div className="col-span-2">
              <Label>Notes</Label>
              <Input value={sd.notes} onChange={(e) => setSd({ ...sd, notes: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button
              onClick={registerSD}
              disabled={busy}
              className="bg-purple-700 hover:bg-purple-800"
              data-testid="sd-inv-save"
            >
              {busy ? "Registering…" : "Register & Recognize Revenue"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


// ============ Bulk Pricing Button (respects each unit's OWN area) ============
function BulkPricingButton({ projectId, units, onDone }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    tower: "", wing: "", floor_from: "", floor_to: "", unit_type: "", configuration: "", status: "",
    rate_per_sqft: "", plc: "", floor_rise_charges: "", other_charges: "",
    gst_amount: "", stamp_duty: "", registration_charges: "", reason: "",
  });

  const towers = useMemo(() => [...new Set(units.map((u) => u.tower).filter(Boolean))], [units]);
  const configs = useMemo(() => [...new Set(units.map((u) => u.configuration).filter(Boolean))], [units]);

  // Preview of matched units with their varying areas
  const matched = useMemo(() => {
    return units.filter((u) => {
      if (form.tower && u.tower !== form.tower) return false;
      if (form.wing && u.wing !== form.wing) return false;
      if (form.unit_type && u.unit_type !== form.unit_type) return false;
      if (form.configuration && u.configuration !== form.configuration) return false;
      if (form.status && u.status !== form.status) return false;
      if (form.floor_from !== "" && u.floor < Number(form.floor_from)) return false;
      if (form.floor_to !== "" && u.floor > Number(form.floor_to)) return false;
      return true;
    });
  }, [form, units]);

  const rate = Number(form.rate_per_sqft) || 0;
  const charges = ["plc", "floor_rise_charges", "other_charges", "gst_amount", "stamp_duty", "registration_charges"]
    .reduce((s, k) => s + (Number(form[k]) || 0), 0);

  async function apply() {
    if (!projectId) return toast.error("Select a project");
    if (!matched.length) return toast.error("No units matched the filters");
    const priceFields = ["rate_per_sqft", "plc", "floor_rise_charges", "other_charges",
      "gst_amount", "stamp_duty", "registration_charges"];
    if (!priceFields.some((k) => form[k] !== "" && !isNaN(Number(form[k])))) {
      return toast.error("Enter at least one price field to apply");
    }
    if (!window.confirm(`Apply pricing to ${matched.length} units? Each unit keeps its own area, so totals will differ per unit.`)) return;

    const payload = { project_id: projectId };
    ["tower", "wing", "unit_type", "configuration", "status", "reason"].forEach((k) => { if (form[k]) payload[k] = form[k]; });
    ["floor_from", "floor_to"].forEach((k) => { if (form[k] !== "") payload[k] = Number(form[k]); });
    priceFields.forEach((k) => { if (form[k] !== "" && !isNaN(Number(form[k]))) payload[k] = Number(form[k]); });

    try {
      const { data } = await api.post("/units/bulk-price", payload);
      toast.success(`Priced ${data.updated} units — each using its own area`);
      onDone?.();
      setOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk pricing failed");
    }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" data-testid="bulk-pricing-btn"><Calculator size={13} className="mr-1" /> Bulk Pricing</Button>
      </DialogTrigger>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading">Bulk Pricing · Each unit uses its own area</DialogTitle>
        </DialogHeader>
        <div className="text-xs text-zinc-500 bg-emerald-50 border border-emerald-200 rounded-md p-2 mb-3">
          Enter a rate per sqft and/or fixed charges. The system will apply these to every matched unit, computing
          <b> Total = rate × THAT unit's own area</b>. Units with different carpet areas will end up with different totals — as expected.
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <div><Label>Tower</Label>
            <Select value={form.tower || " "} onValueChange={(v) => setForm({ ...form, tower: v.trim() })}>
              <SelectTrigger data-testid="bp-tower"><SelectValue placeholder="All towers" /></SelectTrigger>
              <SelectContent>
                <SelectItem value=" ">All towers</SelectItem>
                {towers.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div><Label>Wing</Label><Input value={form.wing} onChange={(e) => setForm({ ...form, wing: e.target.value })} placeholder="All wings" /></div>
          <div><Label>Configuration</Label>
            <Select value={form.configuration || " "} onValueChange={(v) => setForm({ ...form, configuration: v.trim() })}>
              <SelectTrigger data-testid="bp-config"><SelectValue placeholder="All" /></SelectTrigger>
              <SelectContent>
                <SelectItem value=" ">All configurations</SelectItem>
                {configs.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div><Label>Floor From</Label><Input type="number" value={form.floor_from} onChange={(e) => setForm({ ...form, floor_from: e.target.value })} placeholder="Any" /></div>
          <div><Label>Floor To</Label><Input type="number" value={form.floor_to} onChange={(e) => setForm({ ...form, floor_to: e.target.value })} placeholder="Any" /></div>
          <div><Label>Status</Label>
            <Select value={form.status || " "} onValueChange={(v) => setForm({ ...form, status: v.trim() })}>
              <SelectTrigger><SelectValue placeholder="All" /></SelectTrigger>
              <SelectContent>
                <SelectItem value=" ">All statuses</SelectItem>
                {STATUSES.map((s) => <SelectItem key={s} value={s}>{STATUS_LABEL[s]}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="mt-3 border border-zinc-200 rounded-md">
          <div className="bg-zinc-50 px-3 py-2 text-[10px] uppercase tracking-widest font-semibold text-zinc-600">Pricing To Apply</div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 p-3">
            <div><Label>Rate ₹/sqft</Label><Input type="number" value={form.rate_per_sqft} onChange={(e) => setForm({ ...form, rate_per_sqft: e.target.value })} data-testid="bp-rate" /></div>
            <div><Label>PLC (₹)</Label><Input type="number" value={form.plc} onChange={(e) => setForm({ ...form, plc: e.target.value })} /></div>
            <div><Label>Floor Rise (₹)</Label><Input type="number" value={form.floor_rise_charges} onChange={(e) => setForm({ ...form, floor_rise_charges: e.target.value })} /></div>
            <div><Label>Other Charges (₹)</Label><Input type="number" value={form.other_charges} onChange={(e) => setForm({ ...form, other_charges: e.target.value })} /></div>
            <div><Label>GST (₹)</Label><Input type="number" value={form.gst_amount} onChange={(e) => setForm({ ...form, gst_amount: e.target.value })} /></div>
            <div><Label>Stamp Duty (₹)</Label><Input type="number" value={form.stamp_duty} onChange={(e) => setForm({ ...form, stamp_duty: e.target.value })} /></div>
            <div><Label>Registration (₹)</Label><Input type="number" value={form.registration_charges} onChange={(e) => setForm({ ...form, registration_charges: e.target.value })} /></div>
          </div>
        </div>

        <div className="mt-3">
          <Label>Reason (audit)</Label>
          <Input value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} placeholder="e.g., FY26 rate revision" data-testid="bp-reason" />
        </div>

        <div className="mt-3 border border-zinc-200 rounded-md overflow-hidden">
          <div className="bg-zinc-50 px-3 py-2 text-[10px] uppercase tracking-widest font-semibold text-zinc-600 flex justify-between">
            <span>Preview · {matched.length} units matched</span>
            {rate > 0 && <span className="font-mono">Preview uses each unit's own area</span>}
          </div>
          <div className="max-h-56 overflow-y-auto">
            <table className="w-full text-xs">
              <thead className="bg-zinc-50 sticky top-0"><tr>
                <th className="text-left px-2 py-1">Unit</th><th className="text-left px-2 py-1">Config</th>
                <th className="text-right px-2 py-1">Area (sqft)</th>
                <th className="text-right px-2 py-1">Total (rate × area)</th>
                <th className="text-right px-2 py-1">Final Price</th>
              </tr></thead>
              <tbody>
                {matched.slice(0, 60).map((u) => {
                  const area = Number(u.super_built_up_area) || Number(u.built_up_area) || Number(u.carpet_area) || Number(u.area_sqft) || 0;
                  const total = rate > 0 ? rate * area : (Number(u.total_unit_price) || 0);
                  const finalP = total + charges;
                  return (
                    <tr key={u.id} className="border-t border-zinc-100" data-testid={`bp-preview-${u.unit_number}`}>
                      <td className="px-2 py-1 font-mono">{u.unit_number}</td>
                      <td className="px-2 py-1">{u.configuration || u.unit_type}</td>
                      <td className="px-2 py-1 text-right font-mono">{area}</td>
                      <td className="px-2 py-1 text-right font-mono">{inr(total)}</td>
                      <td className="px-2 py-1 text-right font-mono font-semibold">{inr(finalP)}</td>
                    </tr>
                  );
                })}
                {matched.length > 60 && <tr><td colSpan={5} className="text-center py-2 text-zinc-500 italic">…and {matched.length - 60} more units</td></tr>}
                {!matched.length && <tr><td colSpan={5} className="text-center py-6 text-zinc-500">No units match these filters.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={apply} className="bg-emerald-700 hover:bg-emerald-800" data-testid="bp-apply">
            Apply to {matched.length} units
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}